import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { applyPhoneMask, MessageQueryService, skipModalClose, SmartModalService } from '@certemy/ui-components';
import { ComputedFrom } from '@certemy/common';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import _get from 'lodash-es/get';
import { ROLE_CODES } from 'constants/role.constants';
import { Selectors } from 'features/profiles-state/+state/profiles-state.state';
import { InternalIdModalComponent } from 'components/profile-card/modals/internal-id/internal-id.modal.component';
import { IPermissions, PolicyPermissionsService } from 'services/policy-permission/policy-permissions.service';
import { OrgUserProfile } from 'interfaces/profile.interface';
import { EditProfileModalService } from 'components/profile-card/modals/edit-profile/edit-profile.modal.service';
import { UserProfile } from 'services/user/user.interface';
import {
  DEFAULT_PROFILE_CARD_PERMISSIONS,
  PROFILE_CARD_POLICIES,
  PROFILE_CARD_POLICIES_KEYS,
} from './profile-card.constant';
import { ClientServicesService } from 'services/client-services/client-services.service';
import { DOCUMENT } from '@angular/common';
import { setUserClipboardText } from 'utils/helpers';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'cm-profile-card',
  templateUrl: './profile-card.component.html',
  styleUrls: ['./profile-card.component.scss'],
})
export class ProfileCardComponent implements OnInit, OnDestroy {
  @Input() profile: OrgUserProfile;
  @Input() showLoginAs = false;
  @Input() editProfileAllowed = true;
  @Output() loginAs = new EventEmitter<OrgUserProfile>();
  @Output() updateProfile = new EventEmitter<OrgUserProfile>();
  @Input() canUpdateAvatar = false;
  @Input() isGlobalProfile = false;

  permissions: IPermissions<PROFILE_CARD_POLICIES_KEYS> = DEFAULT_PROFILE_CARD_PERMISSIONS;

  currentProfile: UserProfile;

  readonly ROLE_CODES = ROLE_CODES;

  constructor(
    private store: Store<any>,
    private messageQueryService: MessageQueryService,
    private smartModalService: SmartModalService,
    private policyPermissionsService: PolicyPermissionsService,
    private editProfileModalService: EditProfileModalService,
    private clientServicesService: ClientServicesService,
    @Inject(DOCUMENT) private _document: Document,
    private route: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.store
      .select(Selectors.currentProfile)
      .pipe(untilDestroyed(this))
      .subscribe((currentProfile: UserProfile) => {
        this.currentProfile = currentProfile;
      });

    const getOverrides$ = this.isGlobalProfile
      ? this.clientServicesService.getOverrides(this.profile.organization_id)
      : of({ locations: false, titles: false, organizationRoles: false, managers: false });

    forkJoin([
      getOverrides$,
      this.policyPermissionsService.hasMultiplePermissions(Object.values(PROFILE_CARD_POLICIES)).pipe(take(1)),
    ]).subscribe(([overrides, permissions]) => {
      this.permissions = {
        ...permissions,
        locationsFeatureFlag: this.isGlobalProfile ? overrides.locations : permissions.locationsFeatureFlag,
        titlesFeatureFlag: this.isGlobalProfile ? overrides.titles : permissions.titlesFeatureFlag,
        organizationRolesFeatureFlag: this.isGlobalProfile
          ? overrides.organizationRoles
          : permissions.organizationRolesFeatureFlag,
        managersFeatureFlag: this.isGlobalProfile ? overrides.managers : permissions.managersFeatureFlag,
      };
      this.checkQueryParams();
    });
  }

  ngOnDestroy() {}

  private checkQueryParams() {
    setTimeout(() => {
      if (
        _get(this.route.snapshot.queryParams, 'editRole', 'false') === 'true' &&
        this.permissions.personalInfoEdit &&
        this.currentProfile.role_id === ROLE_CODES.ORGANIZATION_ADMIN &&
        [ROLE_CODES.PROFESSIONAL, ROLE_CODES.ORGANIZATION_ADMIN].includes(this.profile.role_id) &&
        this.editProfileAllowed
      ) {
        this.editProfile(this.profile, 'organization-role-container');
      }
    });
  }

  @ComputedFrom('profile', 'currentProfile', 'permissions', 'canUpdateAvatar')
  get isUpdateAvatarAllowed() {
    if (this.currentProfile.profile_id === this.profile.profile_id) {
      return this.canUpdateAvatar;
    }
    return this.permissions.personalInfoEdit && this.canUpdateAvatar;
  }

  @ComputedFrom('profile')
  get mobilePhoneNumber() {
    const countryCode = this.profile.mobileCountryInfo ? `+${this.profile.mobileCountryInfo.countryPhoneCode}` : '';
    return countryCode + applyPhoneMask(this.profile.mobilePhoneNumber);
  }

  onInternalIdUpdate() {
    this.smartModalService
      .open(
        InternalIdModalComponent,
        {
          profile: this.profile,
          isGlobalProfile: this.isGlobalProfile,
        },
        { windowClass: 'sm' },
      )
      .pipe(skipModalClose())
      .subscribe(({ payload }) => {
        this.profile = {
          ...this.profile,
          organization_profile_id: payload.organizationProfileId,
        };
        this.messageQueryService.addMessage('Internal User ID was successfully updated.');
      });
  }

  editProfile(profile, highlightId = '') {
    return this.editProfileModalService.showModalEditProfile(
      profile,
      this.currentProfile,
      this.permissions,
      this.updateProfileData.bind(this),
      this.isGlobalProfile,
      highlightId,
    );
  }

  private updateProfileData(profile: OrgUserProfile) {
    this.profile = {
      ...this.profile,
      ...profile,
    };
    this.updateProfile.emit(this.profile);
  }

  updateAvatar({ profile }) {
    this.updateProfileData(profile);
  }

  contentCopy(event) {
    try {
      const input = event.currentTarget.querySelector('input');
      setUserClipboardText(input);
      this.messageQueryService.addMessage('Email was copied successfully!');
      input.blur();
    } catch (_) {}
  }
}
